import { useApolloClient } from '@apollo/react-hooks'
import {
  ConfirmationDialog,
  FileViewerDialog,
  findFilesOverTextLimit,
  withCloudinary,
  withTranslation
} from '@paintscout/ui'
import type { DialogProps, WithCloudinary, WithTranslation, FileEditorSavePayload } from '@paintscout/ui'
import type { WithClientOptions, WithDialogStackContext, WithUser } from '@paintscout/ui-business'
import { withClientOptions, withDialogStackContext, withUser } from '@paintscout/ui-business'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import { createFile, getFeature, getFiles } from '@paintscout/util/builder'
import { compressFile, getUploadFailureContext, uploadFile } from '@paintscout/util/s3'
import type { CompressFallbackReason, UploadFailureContext } from '@paintscout/util/s3'
import * as Sentry from '@sentry/react'
import type { ApolloClient } from 'apollo-client'
import type { WithSnackbarProps } from 'notistack'
import { withSnackbar } from 'notistack'
import type { QuoteFile } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { useCompanyQuery, useContactQuery, useDealQuery } from '@paintscout/api'
import type { Company, Contact, Deal } from '@paintscout/api'
import type { WithQuoteContext } from '../../context/withQuoteContext'
import { withQuoteContext } from '../../context/withQuoteContext'
import { useFlags } from 'shared/components/common/FlagsProvider'
import AddFileDialog from '../AddFileDialog'
import { UPLOAD_TIMEOUT_MS, stampCompressFallback, withTimeout } from './uploadGuard'
import {
  extractEventError,
  getEventErrorMessage,
  getProviderDisplayName,
  isNotFoundEventError
} from '../../util/integrationEventError'
import {
  COMPANYCAM_PROVIDER,
  canSyncCompanyCamFile,
  getCompanyCamPhotoRef,
  hasCompanyCamSyncChanges,
  rememberDeclinedMarkup,
  resolveCompanyCamSync
} from '../../util/companycamSync'
import { PROCESS_EVENT } from '../../util/processEvent'

const FETCH_POLICY_CACHE_AND_NETWORK = 'cache-and-network' as const

/** Sentry `source` tag for the provider-sync path, so both its report sites group together. */
const SYNC_SENTRY_SOURCE = 'EditItemFilesDialog#syncProviderFile'

/**
 * Internal-only enhancement of QuoteFile for tracking upload state.
 * Equivalent to the legacy EnhancedQuoteFile.
 */
interface EnhancedQuoteFile extends QuoteFile {
  error?: string
  failedFile?: File
}

export type { EnhancedQuoteFile }

export interface DropzoneError {
  error: string
  key?: string
  file?: File
}

export interface EditItemFilesProps {
  /**
   * QuoteFiles to include in dialog
   */
  files?: QuoteFile[]

  /**
   * Automatically starts uploading these files when dialog mounts
   */
  uploadFiles?: File[]

  /**
   * QuoteFiles index to open at
   */
  index?: number

  /**
   * Doc to relate the files to when uploaded
   */
  fileDocId?: string

  /**
   * Only show title input (hides description + Show on)
   */
  titleOnly?: boolean

  /**
   * Label for the caption/description field. Accepted for call-site parity with
   * the legacy editor; the Next editor renders `FileViewerDetails`, which
   * already labels this field "Description", so this prop is unused here.
   */
  captionLabel?: string

  /**
   * Loads files from fileDocId item
   */
  loadFiles?: boolean

  /**
   * Type of fileDocId item
   */
  fileDocType?: string // 'contact' | 'company' | 'deal'

  /**
   * Pre-fetched source-doc files (e.g. linked-estimate files) surfaced as the
   * pull-from-source tab when the in-viewer Upload button reopens AddFileDialog.
   * Forwarded verbatim from whatever opened the media viewer, so the nested
   * dialog offers the same tab the first AddFileDialog did (#21363).
   */
  sourceFiles?: QuoteFile[]

  /**
   * Label override for the source-files tab (e.g. 'Estimate Files'). Paired with
   * `sourceFiles`; mirrors AddFileDialog's `dealFilesLabel` (#21363).
   */
  dealFilesLabel?: string

  /**
   * Scopes the CompanyCam picker's remembered project when this editor isn't
   * inside a quote (e.g. a deal) — pass the doc `_id`. Overrides the
   * quote-derived scope; when omitted it falls back to `quoteContext.quote._id`.
   */
  scopeId?: string

  /**
   * If true, disables any editing functionality
   */
  viewOnly?: boolean

  onConfirm?: (files: QuoteFile[], deletedFiles?: QuoteFile[], fileDoc?: Contact | Company | Deal) => void
  onDelete?: (key: string, files?: EnhancedQuoteFile[]) => void
  onCancel?: () => void
  /**
   * When supplied, the editor's footer shows a "Back" button (instead of "Cancel")
   * that returns to the Media Viewer. Like Cancel, it warns if uploads would be lost.
   */
  onBack?: () => void
  /**
   * Called once per file whose upload fails, with structured diagnostics
   * (failing step, file type/size, device). Callers route this to whatever
   * channel they actually have — e.g. PostHog `track` in painter-view, which is
   * the only place these iPad failures surface (#21104). Fires in addition to
   * the in-dialog Sentry capture + error snackbar.
   */
  onUploadError?: (context: UploadFailureContext) => void
}

type EditItemFilesPropsWithInjections = EditItemFilesProps &
  WithSnackbarProps &
  WithDialogStackContext &
  WithUser &
  WithQuoteContext &
  WithClientOptions &
  WithCloudinary &
  WithTranslation & {
    client: ApolloClient<any>
    open?: boolean
    /** MUI Dialog props passed through from DialogStackRenderer (keepMounted, transitionDuration, hideBackdrop, etc.) */
    dialogProps?: Partial<DialogProps>
    /**
     * LaunchDarkly `s3MediaUpload` flag, injected by the functional wrapper. When
     * true, PDFs/videos uploaded here land on S3 instead of Cloudinary.
     */
    s3MediaUploadEnabled?: boolean
  }

interface EditItemFilesState {
  files: EnhancedQuoteFile[]
  isDirty: boolean
  numQuoteFiles?: number
  /**
   * Controls which file the FileViewer shows. Seeded from the opening `index` and
   * updated as the user browses; a blocked save (over-limit title/caption) forces
   * it to the offending file so the user is taken straight to what needs fixing.
   */
  selectedKey?: string
  /** Key of the file whose provider sync is in flight (#22142). */
  syncingKey?: string
}

class EditItemFilesNext extends React.Component<EditItemFilesPropsWithInjections, EditItemFilesState> {
  static defaultProps: Partial<EditItemFilesPropsWithInjections> = {
    files: [],
    index: 0
  }

  constructor(props: EditItemFilesPropsWithInjections) {
    super(props)

    const { quote } = props.quoteContext
    const { files = [] } = props

    this.state = {
      files,
      numQuoteFiles: quote ? getFiles({ quote, attached: true }).length - files.length : 0,
      isDirty: false,
      selectedKey: files[props.index ?? 0]?.key
    }
  }

  public handleSelectedKeyChange = (key: string) => {
    this.setState({ selectedKey: key })
  }

  // Scopes the CompanyCam picker's remembered project to this doc. Read once
  // here so every openDialog(AddFileDialog, …) call site stays in sync. Prefers
  // an explicit `scopeId` (deal flows, which have no quoteContext) and falls
  // back to the quote's `_id` for estimate flows.
  private get scopeId(): string | undefined {
    return this.props.scopeId ?? this.props.quoteContext?.quote?._id
  }

  // Source doc offered as the "Deal Files" tab when the in-viewer Upload button
  // reopens AddFileDialog. Prefer an explicit fileDoc; otherwise derive the quote's
  // linked deal from quoteContext, so estimate area/line-item/group/option editors —
  // which open the FIRST picker with the deal but hand the editor no fileDoc — keep
  // the tab on the reopened dialog too (#21363 / ENG-767). Mirrors the `scopeId`
  // fallback. Deal/contact/company Files & Notes have no quoteContext and drive the
  // tab via the explicit `sourceFiles` prop instead, so they're unaffected.
  private get addFileSourceDoc(): { fileDocId?: string; fileDocType?: 'contact' | 'company' | 'deal' } {
    const { fileDocId, fileDocType } = this.props
    if (fileDocId || fileDocType) {
      return { fileDocId, fileDocType: fileDocType as 'contact' | 'company' | 'deal' | undefined }
    }
    const dealId = this.props.quoteContext?.quote?.deal_id
    return dealId ? { fileDocId: dealId, fileDocType: 'deal' } : {}
  }

  public componentDidMount() {
    if (this.props.uploadFiles) {
      this.uploadFiles({ files: this.props.uploadFiles })
    }
  }

  public handleCancel = () => {
    const { onCancel, translation } = this.props
    const { isDirty } = this.state

    if (isDirty) {
      this.props.dialogStackContext.openDialog(ConfirmationDialog, {
        message: translation.__('Newly added files will be lost.'),
        onConfirm: () => {
          this.props.dialogStackContext.dismissDialog()
          if (onCancel) onCancel()
        },
        onCancel: () => {
          this.props.dialogStackContext.dismissDialog()
        }
      })
    } else if (onCancel) {
      onCancel()
    }
  }

  public handleBack = () => {
    const { onBack, translation } = this.props
    const { isDirty } = this.state
    if (!onBack) return

    if (isDirty) {
      this.props.dialogStackContext.openDialog(ConfirmationDialog, {
        message: translation.__('Newly added files will be lost.'),
        onConfirm: () => {
          this.props.dialogStackContext.dismissDialog()
          onBack()
        },
        onCancel: () => {
          this.props.dialogStackContext.dismissDialog()
        }
      })
    } else {
      onBack()
    }
  }

  public handleFileChange = (file: EnhancedQuoteFile) => {
    const { files } = this.state
    const fileIndex = files.findIndex(({ key }) => key === file.key)
    if (fileIndex === -1) return

    const [cleanFile] = cleanEnhancedFiles([file])
    this.setState({
      files: [...files.slice(0, fileIndex), cleanFile, ...files.slice(fileIndex + 1)],
      isDirty: true
    })
  }

  public handleFileDelete = (file: EnhancedQuoteFile) => {
    const { __ } = this.props.translation
    this.props.dialogStackContext.openDialog(ConfirmationDialog, {
      title: __('Are you sure?'),
      message: __('This file will be removed.'),
      scary: true,
      onConfirm: () => {
        const { onDelete } = this.props
        const { files } = this.state
        const fileIndex = files.findIndex((f) => f.key === file.key)
        const newFiles = fileIndex !== -1 ? [...files.slice(0, fileIndex), ...files.slice(fileIndex + 1)] : files
        if (onDelete) onDelete(file.key, newFiles)
        this.setState({ files: newFiles, isDirty: true })
        this.props.dialogStackContext.dismissDialog()
      },
      onCancel: () => {
        this.props.dialogStackContext.dismissDialog()
      }
    })
  }

  public handleFilesDelete = (keys: string[]) => {
    if (!keys.length) return
    const keySet = new Set(keys)
    const newFiles = this.state.files.filter((f) => !keySet.has(f.key))
    // Notify onDelete exactly as the single delete does. Estimate item callers
    // write the deletion there (updateFilesForItem with append=false); their
    // onConfirm appends, so a bulk delete that skipped this put every file back
    // on Save (ENG-2186). Once per key, because some callers filter by key.
    const { onDelete } = this.props
    if (onDelete) keys.forEach((key) => onDelete(key, newFiles))
    this.setState({ files: newFiles, isDirty: true })
  }

  public handleDialogConfirm = () => {
    const { onConfirm, translation } = this.props
    const { __ } = translation
    const files = this.state.files
    const filesWithErrors = files.filter((file) => !!file.error)

    // Text that arrived programmatically (e.g. a CompanyCam description) can
    // exceed the field limits that `maxLength` enforces on typed input. Rather
    // than silently truncate and drop potentially important text, block the save,
    // leave the field red, and take the user straight to the first offending photo
    // so they can shorten it themselves (#21295 / ENG-731).
    const filesOverLimit = findFilesOverTextLimit(files)
    if (filesOverLimit.length > 0) {
      this.setState({ selectedKey: filesOverLimit[0].key })
      this.props.enqueueSnackbar(
        filesOverLimit.length > 1
          ? __(
              'Some Media items have a title or description over the character limit. Please shorten them before saving.'
            )
          : __('A Media item has a title or description over the character limit. Please shorten it before saving.'),
        { variant: 'warning' }
      )
      return
    }

    const deletedFiles = this.props.files.filter(
      (file) => !this.state.files.find((stateFile) => stateFile.key === file.key)
    )

    if (filesWithErrors.length > 0) {
      this.props.dialogStackContext.openDialog(ConfirmationDialog, {
        message: __('Some Media items have errors. Are you sure you want to save?'),
        onConfirm: () => {
          if (onConfirm) onConfirm(cleanEnhancedFiles(files), deletedFiles)
          this.props.dialogStackContext.dismissDialog()
        },
        onCancel: this.props.dialogStackContext.dismissDialog
      })
      return
    }

    if (onConfirm) onConfirm(cleanEnhancedFiles(files), deletedFiles)
  }

  public handleNewFiles = async (newFiles: File[]) => {
    if (this.props.user?.isTrial) {
      const filesLeft = TRIAL_QUOTE_FILES_LIMIT - (this.state.numQuoteFiles + this.state.files.length)
      await this.uploadFiles({ files: newFiles.slice(0, filesLeft) })
    } else {
      await this.uploadFiles({ files: newFiles })
    }
  }

  public handleReplaceFile = async (newFile: File, target: QuoteFile) => {
    await this.uploadFiles({ files: [newFile], quoteFiles: [target] })
  }

  /**
   * Change a provider-sourced file (e.g. CompanyCam). Instead of the OS file
   * picker, reopen the provider's own picker so the user can swap the photo.
   */
  public handleChangeProviderFile = (target: QuoteFile) => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { fileDocId, fileDocType } = this.addFileSourceDoc
    openDialog(AddFileDialog, {
      initialTab: 'company-cam',
      fileDocId,
      fileDocType,
      sourceFiles: this.props.sourceFiles,
      dealFilesLabel: this.props.dealFilesLabel,
      scopeId: this.scopeId,
      onUpload: async (uploadFiles: File[]) => {
        dismissDialog()
        if (uploadFiles[0]) await this.handleReplaceFile(uploadFiles[0], target)
      },
      onSelectFiles: (picked: QuoteFile[]) => {
        dismissDialog()
        this.swapProviderFile(target, picked)
      },
      onCancel: dismissDialog
    })
  }

  /**
   * Replace `target` in place with the first picked photo (preserving its key,
   * position and visibility), appending any additional picks as new files.
   */
  public swapProviderFile = (target: QuoteFile, picked: QuoteFile[]) => {
    if (!picked[0]) return
    this.setState((prevState) => {
      const files = swapProviderFileInList(prevState.files, target, picked)
      if (files === prevState.files) return null
      return { files, isDirty: true }
    })
  }

  /**
   * Re-resolve a provider-sourced file against the provider.
   *
   * A CompanyCam photo's `src` is frozen at pick time, so markup added upstream
   * *after* the import never shows up — this is the only user-facing way to
   * refresh it (#22142). One photo per click, on demand: CompanyCam rate-limits,
   * so nothing here runs automatically or in bulk.
   *
   * The refreshed file goes through `handleFileChange`, so like every other edit
   * in this dialog it persists when the user clicks Done.
   */
  public handleSyncProviderFile = async (target: QuoteFile) => {
    const { __ } = this.props.translation
    const ref = getCompanyCamPhotoRef(target)
    if (!ref) return

    // One sync at a time. `syncingKey` only disables the *selected* file's button,
    // so changing selection mid-flight would otherwise let a second sync start —
    // and both completions write through `handleFileChange`, which reads
    // `state.files` outside its updater, so the slower one would clobber the other.
    if (this.state.syncingKey) return

    const providerName = getProviderDisplayName(target.provider, __)
    const syncFailed = () =>
      this.props.enqueueSnackbar(__('Could not sync from %s. Please try again.', providerName), { variant: 'error' })

    this.setState({ syncingKey: target.key })
    try {
      const { data } = await this.props.client.mutate({
        mutation: PROCESS_EVENT,
        variables: {
          type: 'get-photo',
          provider: COMPANYCAM_PROVIDER,
          async: false,
          params: { photoId: ref.photoId }
        },
        fetchPolicy: 'no-cache'
      })

      const eventError = extractEventError(data)
      if (eventError) {
        if (isNotFoundEventError(eventError)) {
          // Expected and benign (the photo was deleted upstream), so it isn't
          // reported. Don't blank the tile either — the image we already have is
          // the last good one.
          this.props.enqueueSnackbar(__('This photo is no longer in %s.', providerName), { variant: 'warning' })
        } else {
          // A returned error token never throws, so without this a recurring
          // provider failure would only ever be a snackbar the user sees once.
          Sentry.captureMessage(`Provider sync failed: ${getEventErrorMessage(eventError) ?? 'unknown error'}`, {
            level: 'warning',
            tags: { fileKey: target.key, provider: target.provider, source: SYNC_SENTRY_SOURCE }
          })
          syncFailed()
        }
        return
      }

      const photo = data?.processEvent?.result?.result
      if (!photo?.id) {
        syncFailed()
        return
      }

      const result = resolveCompanyCamSync({ file: target, photo })
      if (result.status === 'unavailable' || !result.file) {
        this.props.enqueueSnackbar(__('%s has no image for this photo.', providerName), { variant: 'warning' })
        return
      }

      // Only write when the re-resolve actually changed something. A no-op sync
      // must not mark the dialog dirty, or Cancel warns about unsaved changes the
      // user never made (ENG-1118).
      if (hasCompanyCamSyncChanges(target, result.file)) {
        this.handleFileChange(result.file)
      }

      // The file is showing an un-annotated render and CompanyCam has a markup it
      // hasn't been offered. Ask rather than switch: choosing the original was
      // deliberate. Declining records the markup, so the same one is never offered
      // twice — that record persists only once the user clicks Done, like every
      // other edit here.
      if (result.markupAvailable && result.annotatedFile) {
        const { openDialog, dismissDialog } = this.props.dialogStackContext
        openDialog(ConfirmationDialog, {
          title: __('Markup available'),
          message: result.markupIsNew
            ? __('This photo has been marked up in %s since it was added. Use the marked-up version?', providerName)
            : __('This photo has a marked-up version in %s. Use it instead of the original?', providerName),
          yesLabel: __('Use Markup'),
          noLabel: __('Keep Original'),
          onConfirm: () => {
            this.handleFileChange(result.annotatedFile)
            dismissDialog()
          },
          onCancel: () => {
            this.handleFileChange(rememberDeclinedMarkup(result.file, result.markupSrc))
            dismissDialog()
          }
        })
        return
      }

      if (result.status === 'markup-removed') {
        this.props.enqueueSnackbar(__('The markup was removed in %s — showing the original photo.', providerName), {
          variant: 'warning'
        })
      } else if (result.status === 'updated') {
        this.props.enqueueSnackbar(__('Photo updated from %s.', providerName), { variant: 'success' })
      } else {
        this.props.enqueueSnackbar(__('This photo is already up to date.'), { variant: 'info' })
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { fileKey: target.key, provider: target.provider, source: SYNC_SENTRY_SOURCE }
      })
      syncFailed()
    } finally {
      this.setState({ syncingKey: undefined })
    }
  }

  /**
   * Add more files without leaving the viewer. Opens the unified AddFileDialog
   * (device upload + provider tabs) on the tab the user last used, so device and
   * CompanyCam live behind a single Upload button. Picked provider photos are
   * appended to the gallery; OS uploads route through the normal upload path.
   */
  public handleOpenAddDialog = () => {
    const { openDialog, dismissDialog } = this.props.dialogStackContext
    const { fileDocId, fileDocType } = this.addFileSourceDoc
    openDialog(AddFileDialog, {
      // No initialTab — AddFileDialog restores the last-used tab.
      fileDocId,
      fileDocType,
      sourceFiles: this.props.sourceFiles,
      dealFilesLabel: this.props.dealFilesLabel,
      scopeId: this.scopeId,
      onUpload: async (uploadFiles: File[]) => {
        dismissDialog()
        if (uploadFiles.length) await this.handleNewFiles(uploadFiles)
      },
      onSelectFiles: (picked: QuoteFile[]) => {
        dismissDialog()
        this.appendFiles(picked)
      },
      onCancel: dismissDialog
    })
  }

  /**
   * Append picked provider files, skipping any keys already present.
   */
  public appendFiles = (picked: QuoteFile[]) => {
    this.setState((prevState) => {
      const files = appendNewFilesToList(prevState.files, picked)
      if (files === prevState.files) return null
      return { files, isDirty: true }
    })
  }

  /**
   * Persist a drag-reordered file list from the viewer gallery. The new order is
   * committed on Done via the existing onConfirm path.
   *
   * Re-derives each entry from state by key (rather than casting the incoming
   * QuoteFile[] to EnhancedQuoteFile[]) so upload-status fields like
   * `isUploading`/`error` are preserved if a file is reordered mid-upload. Only
   * accepts a true permutation of the current files.
   */
  public handleReorderFiles = (reordered: QuoteFile[]) => {
    this.setState((prevState) => {
      const byKey = new Map(prevState.files.map((f) => [f.key, f]))
      const next = reordered.map((f) => byKey.get(f.key)).filter((f): f is EnhancedQuoteFile => !!f)
      if (next.length !== prevState.files.length) return null
      return { files: next, isDirty: true }
    })
  }

  public handleEditSave = async (payload: FileEditorSavePayload, target: QuoteFile): Promise<QuoteFile> => {
    const newFile = new File([payload.blob], payload.fileName, { type: payload.mimeType })
    try {
      const uploaded = await this.uploadFile(newFile)
      return mergeEditedFile(target, uploaded, payload.mimeType, payload.editState)
    } catch (error) {
      Sentry.captureException(error, { tags: { fileKey: target.key, source: 'EditItemFilesDialog#editSave' } })
      this.props.enqueueSnackbar(this.props.translation.__('Unable to save edited image'), { variant: 'error' })
      throw error
    }
  }

  public uploadFile = async (file: File) => {
    const { clientOptions } = this.props
    const { options } = clientOptions
    const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })

    // Captured during run() and stamped onto a failure (below) so the diagnostic
    // shows whether image compression fell back to the original beforehand.
    let compressFallback: CompressFallbackReason | undefined

    const run = async () => {
      let fileToUpload = file

      if (file.type.startsWith('image') && !file.type.includes('svg')) {
        fileToUpload = await compressFile({ file, onFallback: (reason) => (compressFallback = reason) })
      }

      return uploadFile({
        file: fileToUpload,
        apolloClient: this.props.client,
        uploadOptions: {
          companyId: this.props.user?.user?.app_metadata?.companyId,
          docId: this?.props?.fileDocId ?? this.props.quoteContext?.quote?._id,
          s3UploadFeatureEnabled,
          s3MediaUploadEnabled: this.props.s3MediaUploadEnabled
        }
      })
    }

    try {
      // Include name/size so a timeout is correlatable in logs/Sentry.
      return await withTimeout(run(), UPLOAD_TIMEOUT_MS, `Upload timed out for "${file.name}" (${file.size} bytes)`)
    } catch (error) {
      stampCompressFallback(error, compressFallback)
      throw error
    }
  }

  public uploadFiles = async (args: { files: File[]; quoteFiles?: QuoteFile[] }) => {
    const originalSourceValues = args.quoteFiles
      ? args.quoteFiles.reduce(
          (acc, quoteFile) => ({
            ...acc,
            [quoteFile.key]: { src: quoteFile.src, cloudinaryPublicId: quoteFile.cloudinaryPublicId }
          }),
          {} as Record<string, { src?: string; cloudinaryPublicId?: string; s3PublicKey?: string }>
        )
      : {}

    const editingQuoteFiles: EnhancedQuoteFile[] = args.files.map((file, i) => {
      const quoteFile =
        args.quoteFiles && args.quoteFiles[i]
          ? args.quoteFiles[i]
          : createFile({ type: file.type, title: this.props?.titleOnly ? file.name : '' })
      return {
        ...quoteFile,
        title: file?.name ?? quoteFile.title,
        type: file.type,
        isUploading: true,
        src: null,
        cloudinaryPublicId: null,
        error: null,
        failedFile: null
      }
    })

    const newQuoteFiles = editingQuoteFiles.filter(({ key }) => !this.state.files.find((f) => f.key === key))

    this.setState({
      files: [
        ...this.state.files.map((quoteFile) => {
          const edited = editingQuoteFiles.find((af) => af.key === quoteFile.key)
          return edited ? edited : quoteFile
        }),
        ...newQuoteFiles
      ],
      isDirty: true
    })

    const funcs = editingQuoteFiles.map((quoteFile, index) => async () => {
      try {
        const uploadRes = await this.uploadFile(args.files[index])
        this.setState((prevState) => ({
          files: prevState.files.map((prevQuoteFile) => {
            if (prevQuoteFile.key === quoteFile.key) {
              return {
                ...prevQuoteFile,
                title: uploadRes.title || prevQuoteFile.title,
                height: uploadRes.height,
                width: uploadRes.width,
                // Persist the PDF page count for the painter view only. It
                // expands multi-page PDFs from this stored count via
                // getPdfPageUrls; we keep it on a dedicated `painterPdfPageCount` field
                // (not the shared `pages`) so the in-app/customer FilePreview is
                // unaffected and keeps rendering a single-page thumbnail.
                painterPdfPageCount: uploadRes.pages,
                isUploading: false,
                src: uploadRes.src,
                cloudinaryPublicId: uploadRes?.cloudinaryPublicId ?? '',
                s3PublicKey: uploadRes?.s3PublicKey ?? ''
              }
            }
            return prevQuoteFile
          })
        }))
        return { success: true, key: quoteFile.key }
      } catch (error) {
        const failure = getUploadFailureContext(error, args.files[index])
        // Sentry is a no-op in painter-view (no SDK there), so also hand the
        // structured failure to the caller — painter-view routes it to PostHog,
        // the only channel that surfaces these iPad failures (#21104).
        Sentry.captureException(error, {
          tags: { fileKey: quoteFile.key, source: 'EditItemFilesDialog', uploadStep: failure.uploadStep },
          extra: { ...failure }
        })
        this.props.onUploadError?.(failure)
        this.setState((prevState) => ({
          files: prevState.files.map((prevQuoteFile) => {
            if (prevQuoteFile.key === quoteFile.key) {
              const originalSource = originalSourceValues[prevQuoteFile.key] || {
                src: null,
                cloudinaryPublicId: null,
                s3PublicKey: null
              }
              return {
                ...prevQuoteFile,
                ...originalSource,
                isUploading: false,
                failedFile: args.files[index]
              }
            }
            return prevQuoteFile
          })
        }))
        return { error, key: quoteFile.key }
      }
    })

    const results = await limitConcurrency(funcs, 10)
    const errors = results.filter((res) => !!res?.error)

    if (errors.length === 1) {
      this.props.enqueueSnackbar(this.props.translation.__('Unable to upload file'), { variant: 'error' })
    } else if (errors.length > 1) {
      this.props.enqueueSnackbar(this.props.translation.__('Unable to upload %d files', errors.length), {
        variant: 'error'
      })
    }

    this.setState((prevState) => {
      const errorsByKey = errors.reduce(
        (total, res) => {
          if (res.error) {
            if (res.error.response && res.error.response.data) {
              return { ...total, [res.key]: res.error.response.data.name }
            } else if (res.error.message) {
              return { ...total, [res.key]: res.error.message }
            }
          }
          return total
        },
        {} as Record<string, string>
      )

      return {
        files: prevState.files.map((file) => {
          const error = errorsByKey[file.key] || null
          return {
            ...file,
            isUploading: false,
            error: error ? error : file.error
          }
        })
      }
    })
  }

  public render() {
    const { titleOnly, viewOnly, index, open, dialogProps, onBack, translation } = this.props
    const { __ } = translation
    const { files, selectedKey, syncingKey } = this.state
    const uploading = files.some((file) => file.isUploading)
    const initialKey = files[index ?? 0]?.key

    return (
      <FileViewerDialog
        {...dialogProps}
        open={!!open}
        title={files.length ? __('Edit Media') : __('Add Media')}
        files={files}
        initialKey={initialKey}
        // Controlled selection so a blocked save can jump the viewer to the
        // offending photo. Falls back to the index-derived key until the user
        // browses (or files finish loading), preserving open-at-index behavior.
        selectedKey={selectedKey ?? initialKey}
        onSelectedKeyChange={this.handleSelectedKeyChange}
        titleOnly={titleOnly}
        viewOnly={viewOnly}
        onFileChange={this.handleFileChange}
        onEditSave={this.handleEditSave}
        onUpload={!viewOnly ? this.handleNewFiles : undefined}
        onPickFiles={!viewOnly ? this.handleOpenAddDialog : undefined}
        onReorder={!viewOnly ? this.handleReorderFiles : undefined}
        onChangeFile={!viewOnly ? this.handleReplaceFile : undefined}
        onChangeProviderFile={!viewOnly ? this.handleChangeProviderFile : undefined}
        onSyncProviderFile={!viewOnly ? this.handleSyncProviderFile : undefined}
        canSyncFile={canSyncCompanyCamFile}
        syncingKey={syncingKey}
        onDelete={!viewOnly ? this.handleFileDelete : undefined}
        onDeleteMany={!viewOnly ? this.handleFilesDelete : undefined}
        onClose={this.handleCancel}
        onBack={onBack ? this.handleBack : undefined}
        onDone={this.handleDialogConfirm}
        doneDisabled={uploading}
        doneLoading={uploading}
      />
    )
  }
}

const EditItemFilesWithHOCs = withSnackbar(
  withDialogStackContext(
    withUser(withQuoteContext(withClientOptions(withCloudinary(withTranslation(EditItemFilesNext)))))
  )
)

function EditItemFilesDialogNext(props: DialogProps & EditItemFilesProps) {
  const {
    files = [],
    index,
    uploadFiles,
    loadFiles,
    fileDocType,
    onConfirm,
    titleOnly,
    // Consumed only by the legacy editor's FileView; the Next path renders
    // FileViewerDetails (already labeled "Description"). Destructured here so it
    // isn't spread onto FileViewerDialog via dialogProps.
    captionLabel: _captionLabel,
    fileDocId,
    onDelete,
    onCancel,
    onBack,
    onUploadError,
    viewOnly,
    sourceFiles,
    dealFilesLabel,
    scopeId,
    open,
    ...baseDialogProps
  } = props

  const apolloClient = useApolloClient()
  const { s3MediaUpload } = useFlags()
  const [fileDoc, setFileDoc] = useState(null)
  const [docFiles, setDocFiles] = useState<QuoteFile[]>([])

  const { data: contactData } = useContactQuery({
    variables: { id: fileDocId },
    skip: !loadFiles || fileDocType !== 'contact',
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK
  })

  const { data: companyData } = useCompanyQuery({
    variables: { id: fileDocId },
    skip: !loadFiles || fileDocType !== 'company',
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK
  })

  const { data: dealData } = useDealQuery({
    variables: { id: fileDocId },
    skip: !loadFiles || fileDocType !== 'deal',
    fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK
  })

  useEffect(() => {
    if (contactData) setFileDoc(contactData.contact)
    else if (companyData) setFileDoc(companyData.company)
    else if (dealData) setFileDoc(dealData.deal)
  }, [contactData, companyData, dealData])

  useEffect(() => {
    if (fileDoc) {
      const next = Object.values(fileDoc?.files ?? {}).filter((file: QuoteFile) =>
        files.some((f) => f.key === file.key)
      ) as QuoteFile[]
      setDocFiles(next)
    }
  }, [fileDoc, files])

  const waitingForDoc = loadFiles && !docFiles.length

  return (
    <EditItemFilesWithHOCs
      client={apolloClient}
      open={open && !waitingForDoc}
      dialogProps={baseDialogProps}
      s3MediaUploadEnabled={s3MediaUpload}
      files={loadFiles ? docFiles : files}
      uploadFiles={uploadFiles}
      viewOnly={viewOnly}
      index={index}
      onConfirm={(files, deletedFiles) => onConfirm && onConfirm(files, deletedFiles, fileDoc)}
      onDelete={onDelete}
      onCancel={onCancel}
      onBack={onBack}
      onUploadError={onUploadError}
      titleOnly={titleOnly}
      fileDocId={fileDocId}
      fileDocType={fileDocType}
      sourceFiles={sourceFiles}
      dealFilesLabel={dealFilesLabel}
      scopeId={scopeId}
    />
  )
}

export default EditItemFilesDialogNext

function cleanEnhancedFiles(files: EnhancedQuoteFile[]): QuoteFile[] {
  return files.map(({ error: _error, failedFile: _failedFile, isUploading: _isUploading, ...cleanFile }) => cleanFile)
}

/**
 * Merges an uploaded edit-result back onto its source QuoteFile, stashing the
 * pre-edit src/cloudinaryPublicId/s3PublicKey under `original*` keys.
 *
 * Critical invariant: `original*` fields are only populated the **first** time
 * a file is edited. Subsequent edits must not overwrite them, so the true
 * pre-edit original is preserved for future "revert" UX.
 *
 * Exported for unit testing.
 */
export interface UploadedFileResult {
  src?: string
  cloudinaryPublicId?: string
  s3PublicKey?: string
  width?: number
  height?: number
}

export function mergeEditedFile(
  target: QuoteFile,
  uploaded: UploadedFileResult,
  mimeType?: string,
  editState?: QuoteFile['imageEditState']
): QuoteFile {
  return {
    ...target,
    originalSrc: target.originalSrc || target.src,
    originalCloudinaryPublicId: target.originalCloudinaryPublicId || target.cloudinaryPublicId,
    originalS3PublicKey: target.originalS3PublicKey || target.s3PublicKey,
    src: uploaded.src,
    cloudinaryPublicId: uploaded.cloudinaryPublicId ?? '',
    s3PublicKey: uploaded.s3PublicKey ?? '',
    width: uploaded.width || target.width,
    height: uploaded.height || target.height,
    format: mimeType || target.format,
    type: mimeType || target.type,
    // Stash the editor state so the next open can re-edit non-destructively
    // (reload originalSrc + rehydrate crop/markup). Falls back to any existing
    // state when the editor didn't supply one.
    imageEditState: editState ?? target.imageEditState
  }
}

/**
 * Swaps `target` in `files` for the first picked photo, preserving the target's
 * key/position/visibility and dropping artifacts of the previously-attached
 * image. Additional picks are appended as new files (skipping keys already present).
 * Returns the original array unchanged if `target` isn't found or nothing was picked.
 *
 * Exported for unit testing.
 */
export function swapProviderFileInList(
  files: EnhancedQuoteFile[],
  target: QuoteFile,
  picked: QuoteFile[]
): EnhancedQuoteFile[] {
  const [first, ...rest] = picked
  if (!first) return files

  const index = files.findIndex((f) => f.key === target.key)
  if (index === -1) return files

  const swapped: EnhancedQuoteFile = {
    ...target,
    src: first.src,
    provider: first.provider,
    // The target's key is preserved (below), so it stops describing the photo now
    // attached. These carry the new photo's identity instead, and are what a later
    // Sync re-resolves against (#22142).
    providerId: first.providerId,
    providerVariant: first.providerVariant,
    // The new photo's markup state, not the old one's — a stale record here would
    // make Sync mis-judge whether the swapped-in photo's markup is news.
    providerMarkupSrc: first.providerMarkupSrc,
    // The new photo's downsized PDF render; the old one would print the wrong image (#23666).
    providerPrintSrc: first.providerPrintSrc,
    type: first.type ?? 'image',
    title: target.title || first.title,
    caption: first.caption ?? target.caption,
    // drop artifacts of the previously-attached image
    cloudinaryPublicId: null,
    s3PublicKey: null,
    originalSrc: undefined,
    originalCloudinaryPublicId: undefined,
    originalS3PublicKey: undefined,
    width: undefined,
    height: undefined,
    isUploading: false,
    error: null,
    failedFile: null
  }

  const existingKeys = new Set(files.map((f) => f.key))
  const extras = rest.filter((f) => !existingKeys.has(f.key))

  return [...files.slice(0, index), swapped, ...files.slice(index + 1), ...extras]
}

/**
 * Appends `picked` files to the end of `files`, skipping any keys already
 * present (so re-picking an already-added photo is a no-op). Returns the
 * original array unchanged when there's nothing new to add.
 *
 * Exported for unit testing.
 */
export function appendNewFilesToList(files: EnhancedQuoteFile[], picked: QuoteFile[]): EnhancedQuoteFile[] {
  if (!picked?.length) return files
  const existingKeys = new Set(files.map((f) => f.key))
  const extras = picked.filter((f) => !existingKeys.has(f.key))
  if (!extras.length) return files
  return [...files, ...extras]
}

export async function limitConcurrency(funcs, limit) {
  const results = []
  const queue = [...funcs]

  const worker = async () => {
    while (queue.length > 0) {
      const func = queue.shift()
      if (func) {
        const result = await func().catch((error) => ({ error }))
        results.push(result)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(limit, funcs.length) }, worker))
  return results
}
